import { StatName } from "@/shared/const/glossary";
import { theme } from "@/shared/theme";
import { TeamMetadataRow } from "@suns/api/generated-client/apollo";
import {
  Flex,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@suns/design-system";
import { ChartConfig } from "@suns/design-system/src/components/Chart/Chart";
import { useState } from "react";
import { MetricButton } from "@/pages/players/components/MetricButton";
import { formatTeamChartData } from "@/shared/utils/helper-functions";
import { MultiLineChart } from "@/components";

const chartConfig = {
  tpv: {
    label: StatName.TPV,
    color: theme.colors.purple.DEFAULT,
  },
  oTpv: {
    label: StatName.OTPV,
    color: theme.colors.magenta.DEFAULT,
  },
  dTpv: {
    label: StatName.DTPV,
    color: theme.colors.teal.DEFAULT,
  },
} satisfies ChartConfig;

type MetricKey = keyof typeof chartConfig;

export function TeamPpvChart({
  teamMetadata,
}: {
  teamMetadata: TeamMetadataRow[];
}) {
  const [activeChart, setActiveChart] = useState<
    keyof typeof chartConfig | null
  >(null);
  const [showAdjustedTpv, setShowAdjustedTpv] = useState(false);

  const chartDataMonthly = formatTeamChartData(
    "monthly",
    teamMetadata.filter((data) => data.tpv !== null)
  );
  const chartDataAnnual = formatTeamChartData(
    "annual",
    teamMetadata.filter((data) => data.tpv !== null)
  );

  const onMetricClick = (metric: keyof typeof chartConfig) => {
    if (activeChart === metric) {
      setActiveChart(null);
    } else {
      setActiveChart(metric);
    }
  };

  const getMetricValues = (
    data: typeof chartDataAnnual,
    metric: MetricKey
  ) => ({
    currentValue: data[data.length - 1]?.[metric]?.toString() ?? null,
    previousValue:
      data.find((d) => d[metric] !== null)?.[metric]?.toString() ?? null,
  });

  return (
    <Flex direction="down" gap="md" className="w-full">
      <Tabs defaultValue="annual">
        <Flex direction="right" justify="between">
          <Flex direction="down" gap="sm">
            <Text heading muted size="lg">
              Team Performance Value ({StatName.TPV})
            </Text>
            <Flex direction="right" gap="sm" align="center">
              <Switch
                checked={showAdjustedTpv}
                onCheckedChange={setShowAdjustedTpv}
              />
              <Text size="xs" heading>
                Inflation Adjusted
              </Text>
            </Flex>
          </Flex>
          <TabsList>
            <TabsTrigger value="annual">Annual</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Flex>
        <TabsContent value="annual">
          <Flex direction="down" gap="sm" className="w-full">
            <Flex direction="right" gap="sm" className="flex-wrap">
              {(
                Object.entries(chartConfig) as [
                  MetricKey,
                  (typeof chartConfig)[MetricKey],
                ][]
              ).map(([key, config]) => {
                const values = getMetricValues(chartDataAnnual, key);
                return (
                  <MetricButton
                    key={key}
                    label={config.label}
                    color={config.color}
                    currentValue={values.currentValue}
                    differenceType="percentage"
                    active={activeChart === key}
                    onClick={() => onMetricClick(key)}
                  />
                );
              })}
            </Flex>
            <MultiLineChart
              dateRange="annual"
              chartData={chartDataAnnual.map((data) => ({
                ...data,
                tpv: showAdjustedTpv ? data.adjustedTpv ?? data.tpv : data.tpv,
                oTpv: showAdjustedTpv
                  ? data.adjustedOTpv ?? data.oTpv
                  : data.oTpv,
                dTpv: showAdjustedTpv
                  ? data.adjustedDtpv ?? data.dTpv
                  : data.dTpv,
                snapshotDate: data.snapshotDate ?? "",
              }))}
              chartConfig={chartConfig}
              activeChart={activeChart}
              chartType="currency"
            />
          </Flex>
        </TabsContent>
        <TabsContent value="monthly">
          <Flex direction="down" gap="sm" className="w-full">
            <Flex direction="right" gap="sm" className="flex-wrap">
              {(
                Object.entries(chartConfig) as [
                  MetricKey,
                  (typeof chartConfig)[MetricKey],
                ][]
              ).map(([key, config]) => {
                const values = getMetricValues(chartDataMonthly ?? [], key);
                return (
                  <MetricButton
                    key={key}
                    label={config.label}
                    color={config.color}
                    currentValue={values.currentValue}
                    differenceType="percentage"
                    active={activeChart === key}
                    onClick={() => onMetricClick(key)}
                  />
                );
              })}
            </Flex>

            <MultiLineChart
              dateRange="monthly"
              chartData={chartDataMonthly?.map((data) => ({
                ...data,
                tpv: showAdjustedTpv ? data.adjustedTpv ?? data.tpv : data.tpv,
                oTpv: showAdjustedTpv
                  ? data.adjustedOTpv ?? data.oTpv
                  : data.oTpv,
                dTpv: showAdjustedTpv
                  ? data.adjustedDtpv ?? data.dTpv
                  : data.dTpv,
                snapshotDate: data.snapshotDate ?? "",
              }))}
              chartConfig={chartConfig}
              activeChart={activeChart}
              chartType="currency"
            />
          </Flex>
        </TabsContent>
      </Tabs>
    </Flex>
  );
}
